import { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Send, Loader2, Landmark, User } from 'lucide-react';
import { useAuthStore } from '../../store/authStore';

const API = import.meta.env.VITE_API_URL || '/api/v1';

interface Mensagem {
  id: string;
  conversaId: string;
  remetenteId: string;
  texto: string;
  lida: boolean;
  criadoEm: string;
  remetente: { id: string; nome: string; perfil: string };
}

interface ConversaInfo {
  id: string;
  aluno: { id: string; nome: string };
  professor: { id: string; nome: string } | null;
}

export function ConversaDetalhe() {
  const { id: conversaId } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem('ac_token');
  const user = useAuthStore((s) => s.user);
  const responsavelId = user?.responsavelId;

  const [messages, setMessages] = useState<Mensagem[]>([]);
  const [conversaInfo, setConversaInfo] = useState<ConversaInfo | null>(null);
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [isOffline, setIsOffline] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!responsavelId || !conversaId) return;

    // Load initial messages
    loadMessages();

    // Fetch conversation info from active list (mock/extract or find via API)
    fetchConversaInfo();

    // Setup polling every 3 seconds for real-time sync — guarded by visibility
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') loadMessages();
    }, 3000);

    // Reload immediately when tab becomes visible again
    const onVisible = () => {
      if (document.visibilityState === 'visible') loadMessages();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [responsavelId, conversaId]);

  useEffect(() => {
    // Scroll to bottom when messages load/change
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  async function fetchConversaInfo() {
    try {
      const res = await fetch(`${API}/mobile/responsaveis/${responsavelId}/conversas`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const conversas = await res.json();
        const found = conversas.find((c: any) => c.id === conversaId);
        if (found) {
          setConversaInfo({
            id: found.id,
            aluno: found.aluno,
            professor: found.professor,
          });
        }
      }
    } catch (err: any) {
      window.dispatchEvent(new CustomEvent('ac:client-error', {
        detail: { context: 'ConversaDetalhe.fetchConversaInfo', error: err?.message || 'Erro ao carregar detalhes da conversa' }
      }));
    }
  }

  async function loadMessages() {
    try {
      const res = await fetch(`${API}/mobile/responsaveis/${responsavelId}/conversas/${conversaId}/mensagens`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        setMessages(await res.json());
        setIsOffline(false);
      } else {
        setIsOffline(true);
      }
    } catch (err: any) {
      setIsOffline(true);
      window.dispatchEvent(new CustomEvent('ac:client-error', {
        detail: { context: 'ConversaDetalhe.loadMessages', error: err?.message || 'Falha de conexão com chat' }
      }));
    }
    setLoading(false);
  }

  async function handleSend(e: React.FormEvent) {
    e.preventDefault();
    if (!text.trim() || sending) return;

    setSending(true);
    setSendError(null);
    const textToSend = text;
    setText('');

    try {
      const res = await fetch(`${API}/mobile/responsaveis/${responsavelId}/conversas/${conversaId}/mensagens`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ texto: textToSend }),
      });

      if (res.ok) {
        const newMsg = await res.json();
        setMessages((prev) => [...prev, newMsg]);
      } else {
        setText(textToSend);
        setSendError('Não foi possível enviar a mensagem. Verifique a conexão.');
        window.dispatchEvent(new CustomEvent('ac:client-error', {
          detail: { context: 'ConversaDetalhe.handleSend', error: `HTTP ${res.status}: ${res.statusText}` }
        }));
      }
    } catch (err: any) {
      setText(textToSend);
      setSendError('Falha de conexão. Tente novamente.');
      window.dispatchEvent(new CustomEvent('ac:client-error', {
        detail: { context: 'ConversaDetalhe.handleSend', error: err?.message || 'Erro de rede' }
      }));
    }
    setSending(false);
  }

  if (loading && messages.length === 0) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 80 }}>
        <Loader2 size={28} style={{ animation: 'spin 1s linear infinite', color: '#7c3aed' }} />
      </div>
    );
  }

  const isSecretaria = !conversaInfo?.professor;
  const headerTitle = isSecretaria ? 'Secretaria Escolar' : `Prof. ${conversaInfo?.professor?.nome}`;
  const headerSubtitle = conversaInfo ? `Filho(a): ${conversaInfo.aluno.nome}` : '';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 120px)', margin: '-16px -16px' }}>
      {/* Top Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '12px 16px',
          borderBottom: '1px solid var(--border-color)',
          background: 'white',
          position: 'sticky',
          top: 0,
          zIndex: 10,
        }}
      >
        <button
          onClick={() => navigate('/mobile/conversas')}
          style={{ border: 'none', background: 'none', padding: 4, cursor: 'pointer', display: 'flex', alignItems: 'center', color: '#64748b' }}
        >
          <ArrowLeft size={20} />
        </button>

        <div
          style={{
            width: 38,
            height: 38,
            borderRadius: '50%',
            background: isSecretaria ? '#e0f2fe' : '#ede9fe',
            color: isSecretaria ? '#0284c7' : '#7c3aed',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          {isSecretaria ? <Landmark size={18} /> : <User size={18} />}
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <h4 style={{ fontSize: '0.88rem', fontWeight: 800, color: '#1e293b', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {headerTitle}
            </h4>
            {isOffline && (
              <span style={{ fontSize: '0.65rem', background: '#fee2e2', color: '#dc2626', padding: '1px 6px', borderRadius: 6, fontWeight: 700 }}>
                Reconectando...
              </span>
            )}
          </div>
          <p style={{ fontSize: '0.72rem', color: '#64748b', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {headerSubtitle}
          </p>
        </div>
      </div>

      {/* Messages Area */}
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: '16px',
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
          background: '#f8fafc',
        }}
      >
        {messages.map((m) => {
          const isMine = m.remetenteId === user?.id;
          return (
            <div
              key={m.id}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: isMine ? 'flex-end' : 'flex-start',
                width: '100%',
              }}
            >
              <div
                style={{
                  maxWidth: '75%',
                  padding: '10px 14px',
                  borderRadius: isMine ? '16px 16px 2px 16px' : '16px 16px 16px 2px',
                  background: isMine ? '#7c3aed' : 'white',
                  color: isMine ? 'white' : '#1e293b',
                  fontSize: '0.85rem',
                  fontWeight: 500,
                  boxShadow: '0 2px 5px rgba(0,0,0,0.03)',
                  border: isMine ? 'none' : '1px solid #e2e8f0',
                  wordBreak: 'break-word',
                }}
              >
                {m.texto}
              </div>
              <span
                style={{
                  fontSize: '0.65rem',
                  color: '#94a3b8',
                  marginTop: 3,
                  padding: '0 4px',
                }}
              >
                {new Date(m.criadoEm).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
              </span>
            </div>
          );
        })}
        <div ref={messagesEndRef} />
      </div>

      {sendError && (
        <div
          onClick={() => setSendError(null)}
          style={{
            background: '#fef2f2',
            borderTop: '1px solid #fecaca',
            color: '#dc2626',
            fontSize: '0.76rem',
            padding: '6px 16px',
            textAlign: 'center',
            cursor: 'pointer',
          }}
        >
          {sendError} (toque para fechar)
        </div>
      )}

      {/* Input Form */}
      <form
        onSubmit={handleSend}
        style={{
          padding: '12px 16px',
          background: 'white',
          borderTop: '1px solid var(--border-color)',
          display: 'flex',
          gap: 10,
          alignItems: 'center',
        }}
      >
        <input
          type="text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Digite sua mensagem..."
          style={{
            flex: 1,
            padding: '10px 14px',
            borderRadius: '12px',
            border: '1px solid var(--border-color)',
            background: '#f8fafc',
            color: '#1e293b',
            fontSize: '0.85rem',
            outline: 'none',
          }}
        />
        <button
          type="submit"
          disabled={!text.trim() || sending}
          style={{
            background: text.trim() ? '#7c3aed' : '#e2e8f0',
            color: 'white',
            border: 'none',
            borderRadius: '12px',
            width: 38,
            height: 38,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            flexShrink: 0,
            transition: 'background 0.2s',
          }}
        >
          {sending ? (
            <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
          ) : (
            <Send size={16} />
          )}
        </button>
      </form>
    </div>
  );
}
